import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";

const DAYS = [
    { id: 1, name: "Lunes" },
    { id: 2, name: "Martes" },
    { id: 3, name: "Miércoles" },
    { id: 4, name: "Jueves" },
    { id: 5, name: "Viernes" },
    { id: 6, name: "Sábado" },
    { id: 7, name: "Domingo" },
];

// Opciones que rellenan varios días a la vez
const DAY_GROUPS = [
    { id: "weekdays", name: "Lunes a viernes", days: [1, 2, 3, 4, 5] },
    { id: "weekend", name: "Fin de semana (sábado y domingo)", days: [6, 7] },
];

export default function Availability(props) {
    const {
        formik,
        showWorkerBlock,
    } = props

    const [selectedDay, setSelectedDay] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [error, setError] = useState(null);

    const [slotsSelected, setSlotsSelected] = useState([]);

    const handleAddSlot = () => {
        setError(null);

        if (!selectedDay || !startTime || !endTime) return;

        if (endTime <= startTime) {
            setError("La hora de fin debe ser posterior a la hora de inicio");
            return;
        }

        const group = DAY_GROUPS.find(g => g.id === selectedDay);
        const days = group ? group.days : [selectedDay];

        const newSlots = days
            .filter(day => !slotsSelected.some(
                s => s.dayOfWeek === day && s.startTime === startTime && s.endTime === endTime
            ))
            .map(day => ({ dayOfWeek: day, startTime, endTime }));
        if (newSlots.length === 0) return;

        setSlotsSelected([...slotsSelected, ...newSlots]);
        setStartTime("");
        setEndTime("");
    };

    const handleRemoveSlot = (dayOfWeek, startTime, endTime) => {
        setSlotsSelected(
            slotsSelected.filter(s => !(s.dayOfWeek === dayOfWeek && s.startTime === startTime && s.endTime === endTime))
        );
    };

    // Actualizar formik con los valores seleccionados
    useEffect(() => {
        formik.values.availability = slotsSelected;
    }, [slotsSelected]); // eslint-disable-line

    // Inicializa los valores por defecto desde formik.values.availability (al editar).
    // Depende de formik.values.id porque los datos del usuario llegan de forma
    // asíncrona (getUserApi) después del primer render del formulario.
    useEffect(() => {
        if (Array.isArray(formik.values.availability)) {
            setSlotsSelected(formik.values.availability);
        }
    }, [formik.values.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!showWorkerBlock) return null;

    return (
        <Box border={1} className="p-20 mt-2 box-default">
            <Grid container spacing={3}>
                <Grid item md={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel shrink id="jornada-type-select-label">Tipo de jornada</InputLabel>
                        <Select
                            labelId="jornada-type-select-label"
                            name="jornadaType"
                            value={formik.values.jornadaType || ""}
                            onChange={formik.handleChange}
                            displayEmpty
                            label="Tipo de jornada"
                        >
                            <MenuItem value=""><em>Sin especificar</em></MenuItem>
                            <MenuItem value="completa">Completa</MenuItem>
                            <MenuItem value="parcial">Parcial</MenuItem>
                            <MenuItem value="personalizada">Personalizada</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item md={6}>
                    <TextField
                        variant="outlined"
                        label="Horas semanales contratadas"
                        className="form-control"
                        name="weeklyContractedHours"
                        type="number"
                        value={formik.values.weeklyContractedHours || ""}
                        onChange={formik.handleChange}
                        fullWidth
                        size="small"
                    />
                    {formik.errors.weeklyContractedHours && (
                        <p className="text-100 text-danger mt-1">
                            {formik.errors.weeklyContractedHours}
                        </p>
                    )}
                </Grid>
                {/* El cliente ya no quiere configurar el margen entre servicios por
                    trabajador (de momento) — se deja comentado, no borrado, por si
                    cambia de opinión. Si se reactiva: IaAvailabilityService ya lo
                    ignora (ver evaluateWorkerForSlot/diagnoseWorkerForSlot en
                    ecx-service), así que habría que quitar también ese ignorado ahí.
                <Grid item md={6}>
                    <TextField
                        variant="outlined"
                        label="Margen entre servicios (minutos)"
                        className="form-control"
                        name="serviceMarginMinutes"
                        type="number"
                        value={formik.values.serviceMarginMinutes || ""}
                        onChange={formik.handleChange}
                        fullWidth
                        size="small"
                        helperText="Si se deja en blanco se usa el margen por defecto de la organización"
                    />
                    {formik.errors.serviceMarginMinutes && (
                        <p className="text-100 text-danger mt-1">
                            {formik.errors.serviceMarginMinutes}
                        </p>
                    )}
                </Grid>
                */}
                <Grid item md={6}>
                    <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                        <h3 className="p-0 mt-0">Disponibilidad horaria</h3>
                        <p>
                            - Añada las franjas horarias en las que el trabajador está disponible. Para reflejar un descanso, añada dos franjas en el mismo día.
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel shrink id="day-select-label">Día</InputLabel>
                            <Select
                                labelId="day-select-label"
                                value={selectedDay}
                                onChange={e => setSelectedDay(e.target.value)}
                                displayEmpty
                                label="Día"
                            >
                                <MenuItem value=""><em>Seleccione un día</em></MenuItem>
                                {DAY_GROUPS.map(group => (
                                    <MenuItem key={group.id} value={group.id}><strong>{group.name}</strong></MenuItem>
                                ))}
                                {DAYS.map(day => (
                                    <MenuItem key={day.id} value={day.id}>{day.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Inicio"
                            type="time"
                            size="small"
                            value={startTime}
                            onChange={e => setStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1, minWidth: 120 }}
                        />
                        <TextField
                            label="Fin"
                            type="time"
                            size="small"
                            value={endTime}
                            onChange={e => setEndTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 1, minWidth: 120 }}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleAddSlot}
                            disabled={!selectedDay || !startTime || !endTime}
                            sx={{ mb: 1.5, alignSelf: "flex-end" }}
                        >
                            Añadir
                        </Button>
                    </div>
                    {error && (
                        <p className="text-100 text-danger mt-1">{error}</p>
                    )}
                </Grid>
                <Grid item md={6}>
                    <div style={{ gap: "0px", display: "flex", flexDirection: "column" }}>
                        <h3 className="p-0 mt-0">&nbsp;</h3>

                        {DAYS.map(day => {
                            const daySlots = slotsSelected.filter(s => s.dayOfWeek === day.id);
                            return (
                                <div key={day.id} style={{ marginTop: 10 }}>
                                    <label style={{ fontWeight: 500 }}>{day.name}</label>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: 4 }}>
                                        {daySlots.length === 0 ? (
                                            <Chip label="Sin franjas" variant="outlined" size="small" />
                                        ) : (
                                            daySlots.map((slot, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={`${slot.startTime} - ${slot.endTime}`}
                                                    onDelete={() => handleRemoveSlot(slot.dayOfWeek, slot.startTime, slot.endTime)}
                                                    color="default"
                                                    size="small"
                                                    clickable
                                                    variant="filled"
                                                />
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Grid>
            </Grid>
        </Box>
    )
}
